/**
 * Feb 2026 R293zx · Public share-by-link viewer for GST documents.
 *
 * Route: /share/:token
 *
 * Opens a logged-out window to the signed PDF of a quotation / sales
 * order / invoice, plus any attachments the author bundled. Backed by
 * `/api/v1/gst/public/share/{token}` (auth-less, token-signed).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon, Loader2, AlertTriangle, Paperclip } from "lucide-react";
import DocumentPreview from "@/components/DocumentPreview";

const API = process.env.REACT_APP_BACKEND_URL;

function iconFor(ctype = "") {
  if (ctype.startsWith("image/")) return ImageIcon;
  if (ctype === "application/pdf") return FileText;
  if (ctype.includes("spreadsheet") || ctype.includes("excel") || ctype === "text/csv") return FileSpreadsheet;
  return FileIcon;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function PublicDocumentShare() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, err: null, doc: null, profile: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/v1/gst/public/share/${token}`);
        if (!cancelled) setState({ loading: false, err: null, doc: r.data.doc, profile: r.data.profile });
      } catch (e) {
        const code = e?.response?.status;
        const msg = code === 410
          ? "This link has expired or been revoked by the sender."
          : code === 404
            ? "This share link isn't valid. The sender may have replaced it."
            : "Couldn't load this document. Please contact the sender.";
        if (!cancelled) setState({ loading: false, err: msg, doc: null, profile: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const pdfUrl = `${API}/api/v1/gst/public/share/${token}/pdf`;

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading document…</span>
        </div>
      </div>
    );
  }

  if (state.err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 shadow-sm text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h1 className="text-base font-semibold text-slate-900 mb-1">Link unavailable</h1>
          <p className="text-sm text-slate-600">{state.err}</p>
        </div>
      </div>
    );
  }

  const { doc, profile } = state;
  const atts = doc?.attachments || [];

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4 space-y-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              {(doc?.doc_type || "document").replace("_", " ")}
            </div>
            <div className="text-lg font-semibold text-slate-900" data-testid="share-doc-no">
              {doc?.doc_no || doc?.id}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="share-open-pdf"
            >
              <FileText className="w-3.5 h-3.5" /> Open PDF
            </a>
            <a
              href={pdfUrl + "?dl=1"}
              download={`${doc?.doc_no || "document"}.pdf`}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800"
              data-testid="share-download-pdf"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          </div>
        </div>

        {/* Preview — reuses the same component the authed app shows */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <DocumentPreview doc={doc} profile={profile} />
        </div>

        {/* Attachments */}
        {atts.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4" data-testid="share-attachments">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
              <Paperclip className="w-3.5 h-3.5" />
              Attachments ({atts.length})
            </div>
            <ul className="divide-y divide-slate-100">
              {atts.map((a) => {
                const Icon = iconFor(a.content_type);
                const url = `${API}/api/v1/gst/public/share/${token}/attachments/${a.id}`;
                return (
                  <li key={a.id} className="py-2 flex items-center gap-3" data-testid={`share-att-${a.id}`}>
                    <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{a.filename}</div>
                      <div className="text-[11px] text-slate-500">{fmtSize(a.size)}</div>
                    </div>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-700 hover:text-slate-900 underline"
                    >
                      Open
                    </a>
                    <a
                      href={url}
                      download={a.filename}
                      className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-0.5"
                    >
                      <Download className="w-3 h-3" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="text-center text-[11px] text-slate-400 pt-2 pb-6">
          Shared securely · This document is for the recipient's use only.
        </div>
      </div>
    </div>
  );
}
